/**
 * UserPage `/u/:handle` — a user's PUBLIC landing page: profile header
 * (avatar + @handle + role + joined + bio, with the owner's Edit link), their
 * public games, and the achievement badge wall. Handle→profile resolves
 * through the shared profile directory; handles are stored canonical-lowercase
 * and unique (migration 0022), so the case-folded match is well-defined.
 *
 * The page paints ONCE (the gallery-single-reveal rule): both async inputs —
 * the directory's first snapshot and the public-creations subscription's
 * first emission — gate a single loading state, so the header never flashes
 * before the games grid can render.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { BadgeMetric, Creation } from '@djgg/creations';
import { useAuth, useProfileDirectory, useServices } from '../services/context.js';
import type { PublicUserProfileSummary } from '../services/types.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useNow } from '../hooks/useNow.js';
import { useUserBadgeRows } from '../services/useUserBadges.js';
import { buildGallery, creationToItem } from '../gallery/model.js';
import { BadgeWall } from '../components/BadgeWall.js';
import { GameCard } from '../components/GameCard.js';
import { Mascot } from '../components/Mascot.js';
import { UserAvatar } from '../components/UserAvatar.js';
import { formatTimeAgo } from '../components/relativeTime.js';

export function UserPage() {
  const { handle } = useParams<{ handle: string }>();
  const services = useServices();
  const { user } = useAuth();
  const { profiles, loaded } = useProfileDirectory();
  const now = useNow(60_000);
  // `null` = the subscription has not emitted yet (vs `[]` = genuinely empty).
  const creationDocs = useSubscription<Array<Creation & { id: string }> | null>(
    services.subscribe.creations,
    null,
    [],
  );

  const needle = (handle ?? '').toLowerCase();
  const profile = profiles.find((p) => (p.handle || '').toLowerCase() === needle);
  const earned = useUserBadgeRows(profile?.id);
  const isOwner = !!user && !user.isAnonymous && !!profile && user.uid === profile.id;
  const ownerUid = isOwner ? profile.id : null;

  // OWN-profile decoration (publish gate): games are born unlisted now, so a
  // new creator's public profile is empty with no explanation. On your own
  // page, count your unlisted games (the myCreations membership stream —
  // visitors never subscribe and keep the published-only view).
  const myGames = useSubscription<Array<Creation & { id: string }> | null>(
    (cb) => (ownerUid ? services.subscribe.myCreations(ownerUid, cb) : () => {}),
    null,
    [ownerUid],
  );
  const unpublishedCount = ownerUid
    ? (myGames ?? []).filter((c) => c.visibility === 'unlisted').length
    : 0;

  // Owner-only decoration: my_badge_progress values for the unearned tiles.
  // One-shot fetch (the view doesn't stream); visitors never fetch and never
  // see progress. A failed read just leaves the wall progress-less.
  const [progress, setProgress] = useState<ReadonlyMap<BadgeMetric, number> | null>(null);
  const [summary, setSummary] = useState<PublicUserProfileSummary | null>(null);
  useEffect(() => {
    setSummary(null);
    if (!profile) return;
    let alive = true;
    services
      .userProfileSummary(profile.id)
      .then((value) => {
        if (alive) setSummary(value);
      })
      .catch(() => {
        /* Derived stats are optional decoration; keep unknown values as em dashes. */
      });
    return () => {
      alive = false;
    };
  }, [services, profile]);
  useEffect(() => {
    setProgress(null);
    if (!isOwner) return;
    let alive = true;
    services
      .myBadgeProgress()
      .then((rows) => {
        if (alive) setProgress(new Map(rows.map((r) => [r.metric, r.value])));
      })
      .catch(() => {
        /* progress is decoration — the wall itself still renders */
      });
    return () => {
      alive = false;
    };
  }, [services, isOwner]);

  // The user's public games, gallery-shaped: same projection, same playable
  // predicate, and the gallery's newest-first ordering (one shared model —
  // a public game that isn't listed on `/` isn't listed here either).
  const games = useMemo(() => {
    if (!profile) return [];
    const mine = (creationDocs ?? [])
      .filter((c) => c.creatorUid === profile.id && c.visibility === 'public')
      .map(creationToItem);
    return buildGallery(mine, [], { sort: 'newest', includeNative: false })
      .filter((item) => item.kind === 'creation')
      .sort(
        (a, b) =>
          b.plays - a.plays ||
          b.views - a.views ||
          b.version - a.version ||
          a.title.localeCompare(b.title) ||
          a.slug.localeCompare(b.slug),
      )
      .slice(0, 5);
  }, [creationDocs, profile]);

  if (!loaded || creationDocs === null) {
    return <div className="empty">Loading…</div>;
  }

  if (!profile) {
    return (
      <div className="empty" data-testid="user-page">
        <Mascot context="not-found" />
        No user goes by @{needle}.
      </div>
    );
  }

  const joined = profile.joinedAt
    ? new Date(profile.joinedAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : '';
  const lastActive = summary
    ? summary.lastActiveAt
      ? formatTimeAgo(summary.lastActiveAt, now)
      : 'No activity yet'
    : '—';

  return (
    <div className="container" data-testid="user-page">
      <div className="panel user-page__header">
        <div className="user-page__identity">
          <UserAvatar avatarKey={profile.avatarKey} size={72} />
          <div>
            <h1 className="gallery__title user-page__handle" data-testid="user-page-handle">
              @{profile.handle}
            </h1>
            <div className="user-page__meta">
              {profile.role && (
                <span
                  className={`role-badge${profile.role === 'superadmin' ? ' role-badge--admin' : ''}`}
                >
                  {profile.role}
                </span>
              )}
              {joined && <span className="muted">Joined {joined}</span>}
            </div>
          </div>
          {isOwner && (
            <>
              <span className="header__spacer" />
              <Link className="btn" to="/account" data-testid="user-page-edit">
                Edit profile
              </Link>
            </>
          )}
        </div>
        {profile.bio && <p className="user-page__bio">{profile.bio}</p>}
        <div className="user-page__stats" data-testid="user-page-stats">
          <div><span>Last active</span><strong title={summary?.lastActiveAt ? new Date(summary.lastActiveAt).toLocaleString() : undefined}>{lastActive}</strong></div>
          <div><span>Games created</span><strong>{summary?.gamesCreated ?? '—'}</strong></div>
          <div><span>Games played</span><strong>{summary?.gamesPlayed ?? '—'}</strong></div>
        </div>
        {summary && summary.recentGames.length > 0 && (
          <div className="user-page__recent" data-testid="user-page-recent-games">
            <span>Recently played</span>
            {summary.recentGames.map((game) => (
              <Link key={game.gameKey} to={`/g/${game.slug}`}>{game.title}</Link>
            ))}
          </div>
        )}
      </div>

      <h2 className="panel__title">Popular games</h2>
      {games.length === 0 ? (
        <div className="empty">
          <Mascot context="gallery-empty" />
          No public games yet.
        </div>
      ) : (
        <div className="grid" data-testid="user-page-games">
          {games.map((item) => (
            <GameCard key={item.slug} item={item} />
          ))}
        </div>
      )}
      {unpublishedCount > 0 && (
        <p className="muted" data-testid="unpublished-note">
          {unpublishedCount} unpublished game{unpublishedCount === 1 ? '' : 's'} —{' '}
          <Link to="/studio">in your Studio</Link>. Only published games appear here.
        </p>
      )}

      <h2 className="panel__title">Badges</h2>
      <BadgeWall earned={earned} owner={isOwner} progress={isOwner ? progress ?? undefined : undefined} />
    </div>
  );
}
